import { useState } from 'react';
import { Link } from 'react-router-dom';
import { MdSearch } from 'react-icons/md';
import CompositionPickerDrawer, { CompositionPick } from './CompositionPickerDrawer';
import type { CompositionOption } from '../useCollectionCompositions';
import { CollectionChannel, MessageRef } from '../collectionModel';

export type MessageChoice = MessageRef & { skip: boolean; channel: CollectionChannel };

interface MessageCompositionPickerProps {
    channel: CollectionChannel;
    value: MessageRef & { skip: boolean };
    options: CompositionOption[];
    onChange: (choice: MessageChoice) => void;
    disabled?: boolean;
}

const SelectedLabel = ({ value, selected, onOpen }: {
    value: MessageRef & { skip: boolean };
    selected: CompositionOption | null;
    onOpen: () => void;
}) => {
    if (value.skip) return <span className="truncate italic text-gray-500">Não enviar</span>;
    if (selected) {
        return (
            <Link to={`/communication/message-composition/${selected.uuid}`} target="_blank" rel="noreferrer"
                title={`Abrir ${selected.name}`} className="truncate text-blue-600 hover:underline dark:text-blue-400">
                {selected.active ? selected.name : `${selected.name} (Inativa)`}
            </Link>
        );
    }
    if (value.messageCompositionId) return <span className="truncate text-gray-500">{value.messageCompositionId} (indisponível)</span>;
    return (
        <button type="button" onClick={onOpen} className="truncate text-left text-amber-700 hover:underline dark:text-amber-300">
            Escolher composição
        </button>
    );
};

const MessageCompositionPicker = ({ channel, value, options, onChange, disabled = false }: MessageCompositionPickerProps) => {
    const [open, setOpen] = useState(false);
    const selected = options.find(o => o.sendId === value.messageCompositionId) ?? null;
    const pending = !value.skip && !value.messageCompositionId;

    const pick = (choice: CompositionPick) => {
        setOpen(false);
        if (choice.skip) {
            onChange({ skip: true, messageCompositionId: null, channel });
            return;
        }
        onChange({ skip: false, messageCompositionId: choice.option.sendId, channel: choice.option.channel as CollectionChannel });
    };

    return (
        <div className={`flex min-w-0 items-center gap-2 border-b-2 py-2 text-sm ${
            pending ? 'border-amber-400' : 'border-gray-300 dark:border-gray-600'
        }`}>
            <div className="flex min-w-0 flex-1">
                <SelectedLabel value={value} selected={selected} onOpen={() => setOpen(true)} />
            </div>
            <button type="button" disabled={disabled} onClick={() => setOpen(true)}
                aria-label="Escolher composição da mensagem" title="Escolher composição da mensagem"
                className="shrink-0 rounded-md p-1 text-gray-500 hover:bg-blue-50 hover:text-blue-600 disabled:opacity-50 dark:hover:bg-slate-700">
                <MdSearch size={18} />
            </button>
            <CompositionPickerDrawer open={open} options={options} selectedSendId={value.messageCompositionId}
                onPick={pick} onClose={() => setOpen(false)} />
        </div>
    );
};

export default MessageCompositionPicker;
